import { Button, CodeInput, CodeInputRef, getChannel, useTheme } from '@ytl/common-web';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../../../contexts/languageContext';
import {
  getAnalytics,
  InitParams,
  StepperControls,
  VerificationPhase,
} from '../../../../modules/verificationComponent/VerificationComponent';
import { getConfig } from '../../../../shared/data/config';
import { WidgetEvent } from '../../../@types/widgetEvents';
import { verificatorResource } from '../../../resource/verificator.resource';
import { emitProcessPhase, emitWidgetEvent } from '../../../utils/emitData';

const RESEND_COOLDOWN_SECONDS = getConfig().verificationCodeResendTimer ?? 30;

type EmailConfirmProps = {
  stepper: StepperControls;
  initParams?: InitParams | null;
  email?: string;
  sessionId: string;
  setSessionId?: (sessionId: string) => void;
  skipGetEmail?: boolean;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isFinished: boolean;
  corrId?: string;
  disableBackButton?: boolean;
  enableAnalytics?: boolean;
};

export const EmailConfirm = ({
  stepper,
  email,
  sessionId,
  initParams,
  isLoading,
  setIsLoading,
  isFinished,
  skipGetEmail = false,
  corrId,
  disableBackButton = false,
  enableAnalytics = false,
}: EmailConfirmProps) => {
  const { translation, language } = useLanguage();
  const { isWebView } = useTheme();

  const borgAnalytics = getAnalytics(enableAnalytics);

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN_SECONDS);
  const [resendCounter, setResendCounter] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | undefined>(undefined);
  const codeInputRef = useRef<CodeInputRef>(null);

  useEffect(() => {
    emitProcessPhase('EmailConfirm');
  }, []);

  useEffect(() => {
    borgAnalytics.sendVirtualPageView({
      channel: getChannel(isWebView),
      virtualName: 'Email authentication',
    });
  }, [borgAnalytics, isWebView]);

  // Send email verification code on mount if GetEmail was skipped
  const initialCodeSentRef = useRef(false);
  useEffect(() => {
    const sendInitialCode = async () => {
      if (skipGetEmail && !initialCodeSentRef.current && sessionId && email) {
        initialCodeSentRef.current = true;
        try {
          // First validate the email
          const verifyResponseEmail = await verificatorResource.validateEmail(
            {
              sessionId,
              email,
              password: '',
            },
            corrId,
          );
          if (verifyResponseEmail.data.status === 'invalid') {
            console.log('Invalid email');
            initialCodeSentRef.current = false; // Allow retry
            return;
          }

          // Then send the verification code
          await verificatorResource.sendEmailVerificationCode(
            {
              sessionId,
              clientId: initParams?.clientID,
            },
            corrId,
          );
          borgAnalytics.sendRegistrationEvent({
            channel: getChannel(isWebView),
            virtualName: 'Email authentication code',
            action: 'EmailVerificationCodeSent',
            result: 'true',
          });
          emitWidgetEvent(
            {
              eventName: WidgetEvent.EmailVerificationCodeSent,
              eventValue: { sessionId: sessionId },
              isSuccess: true,
            },
            { sessionId },
          );
        } catch (err) {
          console.error('Failed to send initial email verification code:', err);
          initialCodeSentRef.current = false; // Allow retry on error
        }
      }
    };
    sendInitialCode();
  }, [skipGetEmail, sessionId, email, corrId, initParams?.clientID, borgAnalytics, isWebView]);

  useEffect(() => {
    if (resendTimer <= 0) return;

    const interval = setInterval(() => {
      setResendTimer(prev => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleResendCode = useCallback(async () => {
    if (resendTimer > 0 || !email) return;

    setIsLoading(true);
    try {
      const verifyResponseSendCode = await verificatorResource.sendEmailVerificationCode(
        {
          sessionId: sessionId,
          lang: initParams?.language || language,
          clientId: initParams?.clientID,
        },
        corrId,
      );

      // Check for HTTP error status codes (4xx and 5xx)
      if (verifyResponseSendCode.status >= 400) {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'sendEmailVerificationCode',
          action: 'TechnicalError',
          result: 'false',
          reason: `HTTP Error ${verifyResponseSendCode.status}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'sendEmailVerificationCode',
              details: `HTTP Error ${verifyResponseSendCode.status}: ${verifyResponseSendCode.statusText}`,
            },
            isSuccess: false,
          },
          { sessionId },
        );
        setIsLoading(false);
        return;
      }

      // Check for application-level errors in response data
      if (verifyResponseSendCode.data.status === 'error') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'sendEmailVerificationCode',
          action: 'TechnicalError',
          result: 'false',
          reason: `${verifyResponseSendCode.data}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'sendEmailVerificationCode',
              details: verifyResponseSendCode.data,
            },
            isSuccess: false,
          },
          { sessionId },
        );
        setIsLoading(false);
        return;
      }

      if (verifyResponseSendCode.data.status === 'banned' || verifyResponseSendCode.data.status === 'limit_reached') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'Temporary termination email',
          action: 'ProcessFinished',
          result: 'false',
          reason: 'banned',
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.EmailVerificationCodeReSent,
            eventValue: { reason: verifyResponseSendCode.data.status },
            isSuccess: false,
          },
          { sessionId },
        );
        setIsLoading(false);
        stepper.goToPhase(VerificationPhase.Banned);
        return;
      }
      setAttemptsRemaining(undefined);
      emitWidgetEvent(
        {
          eventName: WidgetEvent.EmailVerificationCodeReSent,
          eventValue: { counter: resendCounter + 1 },
          isSuccess: true,
        },
        { sessionId },
      );
      borgAnalytics.sendRegistrationEvent({
        channel: getChannel(isWebView),
        virtualName: 'Email authentication code',
        action: 'EmailVerificationCodeSent',
        result: 'true',
        reason: `counter: ${resendCounter + 1}`,
      });
      setResendCounter(prev => prev + 1);
      codeInputRef.current?.focusFirst();
      setError('');
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to resend email verification code:', err);
      borgAnalytics.sendRegistrationEvent({
        channel: getChannel(isWebView),
        virtualName: 'sendEmailVerificationCode',
        action: 'TechnicalError',
        result: 'false',
        reason: `${err}`,
      });
      emitWidgetEvent(
        {
          eventName: WidgetEvent.TechnicalError,
          eventValue: {
            process: 'reSendEmailVerificationCode',
            details: err,
          },
          isSuccess: false,
        },
        { sessionId },
      );
      setIsLoading(false);
    }

    setResendTimer(RESEND_COOLDOWN_SECONDS);
  }, [
    resendTimer,
    email,
    sessionId,
    stepper,
    resendCounter,
    language,
    initParams,
    setIsLoading,
    corrId,
    borgAnalytics,
    isWebView,
  ]);

  useEffect(() => {
    if (isLoading === false) {
      codeInputRef.current?.focusFirst();
    }
  }, [isLoading]);

  const handleCodeComplete = useCallback(
    async (completedCode: string) => {
      setIsLoading(true);
      codeInputRef.current?.clear();
      if (!sessionId) {
        setError(translation.yeppRegistration.webcomponent.expiredCode(completedCode));
        setIsLoading(false);
        return;
      }

      try {
        const verifyResponseCheckCode = await verificatorResource.checkEmailVerificationCode(
          {
            sessionId,
            completedCode,
          },
          corrId,
        );

        // Check for HTTP error status codes (4xx and 5xx)
        if (verifyResponseCheckCode.status >= 400) {
          borgAnalytics.sendRegistrationEvent({
            channel: getChannel(isWebView),
            virtualName: 'checkEmailVerificationCode',
            action: 'TechnicalError',
            result: 'false',
            reason: `HTTP Error ${verifyResponseCheckCode.status}`,
          });
          emitWidgetEvent(
            {
              eventName: WidgetEvent.TechnicalError,
              eventValue: {
                process: 'checkEmailVerificationCode',
                details: `HTTP Error ${verifyResponseCheckCode.status}: ${verifyResponseCheckCode.statusText}`,
              },
              isSuccess: false,
            },
            { sessionId },
          );
          setIsLoading(false);
          return;
        }

        // Check for application-level errors in response data
        if (verifyResponseCheckCode.data.status === 'error') {
          borgAnalytics.sendRegistrationEvent({
            channel: getChannel(isWebView),
            virtualName: 'checkEmailVerificationCode',
            action: 'TechnicalError',
            result: 'false',
            reason: `${verifyResponseCheckCode.data}`,
          });
          emitWidgetEvent(
            {
              eventName: WidgetEvent.TechnicalError,
              eventValue: {
                process: 'checkEmailVerificationCode',
                details: verifyResponseCheckCode.data,
              },
              isSuccess: false,
            },
            { sessionId },
          );
          setIsLoading(false);
          return;
        }

        if (verifyResponseCheckCode.data.status === 'expired') {
          setError(translation.yeppRegistration.webcomponent.expiredCode(completedCode));
          borgAnalytics.sendRegistrationEvent({
            channel: getChannel(isWebView),
            virtualName: 'Email authentication code',
            action: 'EmailVerificationStatus',
            result: 'false',
            reason: 'expired',
          });
          emitWidgetEvent(
            {
              eventName: WidgetEvent.EmailVerificationStatus,
              eventValue: {},
              isSuccess: false,
            },
            { sessionId },
          );
          setIsLoading(false);
          return;
        }

        if (verifyResponseCheckCode.data.status === 'invalid') {
          setAttemptsRemaining(verifyResponseCheckCode.data.attemptsRemaining);
          if (verifyResponseCheckCode.data.attemptsRemaining !== 0) {
            setError(
              translation.yeppRegistration.webcomponent.invalidCode(
                completedCode,
                verifyResponseCheckCode.data.attemptsRemaining,
              ),
            );
          } else {
            setError(translation.yeppRegistration.webcomponent.tooManyAttempts(completedCode));
          }
          borgAnalytics.sendRegistrationEvent({
            channel: getChannel(isWebView),
            virtualName: 'Email authentication code',
            action: 'EmailVerificationStatus',
            result: 'false',
            reason: 'invalid',
          });
          emitWidgetEvent(
            {
              eventName: WidgetEvent.EmailVerificationStatus,
              eventValue: {},
              isSuccess: false,
            },
            { sessionId },
          );
          setIsLoading(false);
          return;
        }
        if (verifyResponseCheckCode.data.status === 'limit_reached') {
          setAttemptsRemaining(0);
          setError(translation.yeppRegistration.webcomponent.tooManyAttempts(completedCode));
          borgAnalytics.sendRegistrationEvent({
            channel: getChannel(isWebView),
            virtualName: 'Email authentication code',
            action: 'EmailVerificationStatus',
            result: 'false',
            reason: 'limit_reached',
          });
          emitWidgetEvent(
            {
              eventName: WidgetEvent.EmailVerificationStatus,
              eventValue: {},
              isSuccess: false,
            },
            { sessionId },
          );
          setIsLoading(false);
          return;
        }
        if (verifyResponseCheckCode.data.status === 'banned') {
          borgAnalytics.sendRegistrationEvent({
            channel: getChannel(isWebView),
            virtualName: 'Temporary termination email',
            action: 'ProcessFinished',
            result: 'false',
            reason: 'banned',
          });
          emitWidgetEvent(
            {
              eventName: WidgetEvent.EmailVerificationStatus,
              eventValue: {},
              isSuccess: false,
            },
            { sessionId },
          );
          setIsLoading(false);
          stepper.goToPhase(VerificationPhase.Banned);
          return;
        }

        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'Email authentication code',
          action: 'EmailVerificationStatus',
          result: 'true',
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.EmailVerificationStatus,
            eventValue: {},
            isSuccess: true,
          },
          { sessionId },
        );
        stepper.goToNext();
      } catch (err) {
        console.error('Failed to check email verification code:', err);
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'checkEmailVerificationCode',
          action: 'TechnicalError',
          result: 'false',
          reason: `${err}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'checkEmailVerificationCode',
              details: err,
            },
            isSuccess: false,
          },
          { sessionId },
        );
        setError(translation.yeppRegistration.webcomponent.generalError);
        setIsLoading(false);
      }
    },
    [stepper, sessionId, translation, setIsLoading, corrId, borgAnalytics, isWebView],
  );

  const handleCodeChange = useCallback((value: string) => {
    setCode(value);
    setError('');
  }, []);

  const handleBack = useCallback(() => {
    emitWidgetEvent(
      {
        eventName: WidgetEvent.EmailVerificationBack,
        eventValue: {},
        isSuccess: false,
      },
      { sessionId },
    );
    stepper.goToPrevious();
  }, [stepper, sessionId]);

  return (
    <div className={`verification-step verification-step--otp${isWebView ? ' verification-step--webview' : ''}`}>
      <div className={'verification-step__code-input'}>
        <CodeInput
          ref={codeInputRef}
          value={code}
          onChange={handleCodeChange}
          onComplete={handleCodeComplete}
          hasError={!!error}
          autoFocus
          disabled={isLoading || isFinished || attemptsRemaining === 0}
        />
      </div>

      {error && <div className="verification-step__container__error">{error}</div>}

      <div className="verification-step__resend verification-step__resend--compact">
        <Button
          className="verification-step__resend__button"
          variant={'text-undecorated'}
          onClick={handleResendCode}
          disabled={resendTimer > 0 || isLoading || isFinished}
        >
          {translation.yeppRegistration.webcomponent.requestNewCode}
        </Button>
        {resendTimer > 0 && (
          <span className="verification-step__resend__timer">
            ({resendTimer} {translation.yeppRegistration.webcomponent.timerSeconds})
          </span>
        )}
      </div>

      {!skipGetEmail && !disableBackButton && (
        <div className={`verification-step__actions${isWebView ? ' verification-step__actions--webview' : ''}`}>
          <Button
            variant={'text-undecorated'}
            onClick={handleBack}
            disabled={isLoading || isFinished}
          >
            {translation.yeppRegistration.webcomponent.back}
          </Button>
        </div>
      )}
    </div>
  );
};
